import { useTranslation } from '@/i18n';
import { Tab } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import styles from './ReportTypeEditTabs.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  activeTab: string;
  reportTypeId: string;
  showCustomFields: boolean;
  isReorderingMode: boolean;
  isOrderChanged: boolean;
  confirmCancelReordering: () => Promise<boolean>;
  skipBrowserConfirm?: () => void;
  onFinishedLoading?: () => void;
};

type ReportTypeTab = {
  label: string;
  redirectUrl: string;
  value: string;
};

export const ReportTypeEditTabs: FC<Props> = memo(
  ({
    activeTab,
    reportTypeId,
    showCustomFields,
    isReorderingMode,
    isOrderChanged,
    confirmCancelReordering,
    skipBrowserConfirm,
    onFinishedLoading,
  }) => {
    const navigateToUrl = (url: string) => {
      location.href = url;
    };
    const { t } = useTranslation();

    useEffect(() => {
      if (typeof onFinishedLoading === 'function') {
        onFinishedLoading();
      }
    }, [onFinishedLoading]);

    const handleTabClick = useCallback(
      async (tab: ReportTypeTab) => {
        if (isReorderingMode && isOrderChanged && confirmCancelReordering) {
          const isConfirmed = await confirmCancelReordering();
          if (isConfirmed) {
            skipBrowserConfirm?.();
            navigateToUrl(tab.redirectUrl);
          }
        } else {
          navigateToUrl(tab.redirectUrl);
        }
      },
      [
        confirmCancelReordering,
        isReorderingMode,
        isOrderChanged,
        skipBrowserConfirm,
      ]
    );

    const reportTypeTabs: ReportTypeTab[] = useMemo(() => {
      return [
        {
          label: t('report_types.tabs.edit'),
          value: 'edit',
          redirectUrl: `/report_types/${reportTypeId}/edit`,
        },
        ...(showCustomFields
          ? [
              {
                label: t('report_types.tabs.transaction_items'),
                value: 'transaction_items',
                redirectUrl: `/report_types/${reportTypeId}/transaction_items`,
              },
            ]
          : []),
        {
          label: t('report_types.tabs.edit_control'),
          value: 'edit_control',
          redirectUrl: `/report_types/${reportTypeId}/edit_control/edit`,
        },
      ];
    }, [t, reportTypeId, showCustomFields]);

    return (
      <div className={cx(styles['tabs-container'])} data-skip-cancel-confirm>
        {reportTypeTabs.map((tab: ReportTypeTab) => (
          <Tab
            key={tab.label}
            isSelected={activeTab === tab.value}
            onClick={() => handleTabClick(tab)}
          >
            <div>{tab.label}</div>
          </Tab>
        ))}
      </div>
    );
  }
);

ReportTypeEditTabs.displayName = 'ReportTypeEditTabs';
